import { MapFilters, NearbyVenue } from '../../types/liveMap'

/**
 * Pure helpers to compute the per-venue stat counts for the map markers.
 * Ported 1:1 from the mobile app's `MapVenueStatFilters.filteredPinCounts`.
 *
 * The result is a 6-slot array aligned to PIN_COLORS / PIN_DISPLAY_PRIORITY:
 *   [0] check-ins, [1] events+visit-plans, [2] breadcrumbs, [3] recent check-ins,
 *   [4] recent views, [5] stickers.
 * Each slot is the count (when the filter is active and > 0) or `null`.
 */

/** Returns n when it's a positive number, otherwise null (mobile `_positive`). */
export function positive(raw?: number | null): number | null {
  const n = raw ?? 0
  return n > 0 ? n : null
}

/**
 * Recent-views count. Prefers the dedicated `peeks` / `interestedUsers` field;
 * falls back to scanning aggregates for a name containing "view" or "peek".
 * Mirrors mobile `_extractViewsCount`.
 */
export function extractViewsCount(v: NearbyVenue): number {
  const p = v.peeks ?? v.interestedUsers
  if (p != null) return p

  const aggs = v.aggregates
  if (!aggs || aggs.length === 0) return 0

  for (const a of aggs) {
    const name = (a.name ?? '').trim().toLowerCase()
    if (name.includes('view') || name.includes('peek')) return a.value
  }
  return 0
}

/**
 * Combined events (RSVP) + visit-plan count. Visit plans are subordinate to
 * events: they only count when the events filter is also on. Mirrors mobile
 * `_eventsAndPlansCount`.
 */
function eventsAndPlansCount(v: NearbyVenue, f: MapFilters): number | null {
  const effectiveVisitPlans = f.events && f.visitPlans
  if (!f.events && !effectiveVisitPlans) return null

  let sum = 0
  if (f.events) {
    sum += v.eventRsvpCount ?? 0
    // Legacy fallback: when the split counts are absent, use the combined field.
    if (v.eventRsvpCount == null && v.visitPlanCount == null) {
      return positive(v.rsvpCount)
    }
  }
  if (effectiveVisitPlans) {
    sum += v.visitPlanCount ?? 0
  }
  if (!f.events && effectiveVisitPlans) {
    sum = v.visitPlanCount ?? 0
  }
  return sum > 0 ? sum : null
}

/** Builds the 6-slot count array for a venue under the active filters. */
export function computePinCounts(v: NearbyVenue, f: MapFilters): (number | null)[] {
  return [
    f.checkIns ? positive(v.totalCheckins) : null,
    eventsAndPlansCount(v, f),
    f.breadcrumbs ? positive(v.breadcrumbCount) : null,
    f.recentCheckIns ? positive(v.recentCheckins) : null,
    f.recentViews ? positive(extractViewsCount(v)) : null,
    f.stickers ? positive(v.stickerCount) : null,
  ]
}
